"use strict";
import { useEffect, useState } from "react";
import { Switch as TamaguiSwitch } from "tamagui";
import { Check } from "ui/src/components/icons";
import { Flex } from "ui/src/components/layout";
import { useSporeColors } from "ui/src/hooks/useSporeColors";
import { spacing } from "ui/src/theme";
const animationProp = {
  animation: [
    "80ms-ease-in-out",
    {
      backgroundColor: {
        overshootClamping: true
      }
    }
  ]
};
export function Switch({
  checked: checkedProp,
  onCheckedChange: onCheckedChangeProp,
  disabled,
  variant,
  switchSize = "large",
  disabledStyle,
  ...rest
}) {
  const [checked, setChecked] = useState(checkedProp);
  const colors = useSporeColors();
  const isBranded = variant === "branded";
  const switchSizes = {
    small: {
      SWITCH_THUMB_HEIGHT: 15,
      SWITCH_TRACK_HEIGHT: 15 + 4 * 2,
      SWITCH_TRACK_WIDTH: spacing.spacing36,
      CHECK_SIZE: 10
    },
    medium: {
      SWITCH_THUMB_HEIGHT: spacing.spacing20,
      SWITCH_TRACK_HEIGHT: spacing.spacing20 + spacing.spacing6,
      SWITCH_TRACK_WIDTH: spacing.spacing48,
      CHECK_SIZE: 12
    },
    large: {
      SWITCH_THUMB_HEIGHT: spacing.spacing24,
      SWITCH_TRACK_HEIGHT: spacing.spacing24 + spacing.spacing8,
      SWITCH_TRACK_WIDTH: spacing.spacing60,
      CHECK_SIZE: 14
    }
  };
  const SWITCH_THUMB_HEIGHT = switchSizes[switchSize].SWITCH_THUMB_HEIGHT;
  const SWITCH_TRACK_HEIGHT = switchSizes[switchSize].SWITCH_TRACK_HEIGHT;
  const SWITCH_TRACK_WIDTH = switchSizes[switchSize].SWITCH_TRACK_WIDTH;
  const CHECK_SIZE = switchSizes[switchSize].CHECK_SIZE;
  useEffect(() => {
    setChecked(checkedProp);
  }, [checkedProp]);
  const onCheckedChange = (val) => {
    if (typeof checkedProp === "undefined") {
      setChecked(val);
    }
    onCheckedChangeProp?.(val);
  };
  const isDisabledStyling = disabled && !checked;
  const frameBackgroundColor = (() => {
    if (isDisabledStyling) {
      return "$surface3";
    }
    if (isBranded) {
      return checked ? "$absGreen" : "$surface3Solid";
    }
    return checked ? "$absGreen" : "$surface3Hovered";
  })();
  const thumbBackgroundColor = (() => {
    if (isDisabledStyling) {
      if (isBranded) {
        return checked ? "$neutral2" : "$neutral3";
      }
      return checked ? "$neutral2" : "$neutral3";
    }
    return checked ? "$neutral2" : "$neutral1";
  })();
  const iconColor = (() => {
    if (isDisabledStyling) {
      return colors.white.val;
    }
    return isBranded ? colors.surface2.val : colors.surface2Hovered.val;
  })();
  const frameActiveStyle = {
    x: checked ? -2 : 0
  };
  const outerActiveStyle = {
    width: SWITCH_THUMB_HEIGHT + 4,
    x: checked ? -4 : 0
  };
  const OUTER_RING_DISTANCE = -6;
  const INNER_RING_DISTANCE = -5;
  return <TamaguiSwitch
    alignItems="center"
    {...animationProp}
    aria-disabled={disabled}
    aria-selected={checked}
    backgroundColor={frameBackgroundColor}
    borderWidth="$none"
    checked={checked}
    defaultChecked={checked}
    group="item"
    hoverStyle={{
      // backgroundColor: isBranded
      //   ? checked
      //     ? '$surface3Hovered'
      //     : '$neutral3Hovered'
      //   : checked
      //     ? '$surface3Hovered'
      //     : '$neutral3Hovered',
      cursor: "pointer"
    }}
    justifyContent="center"
    minHeight={SWITCH_TRACK_HEIGHT}
    minWidth={SWITCH_TRACK_WIDTH}
    p="$spacing4"
    pointerEvents={disabled ? "none" : "auto"}
    disabledStyle={{
      ...checked && { opacity: 0.6 },
      ...disabledStyle
    }}
    onCheckedChange={disabled ? void 0 : onCheckedChange}
    {...rest}
  >
      <TamaguiSwitch.Thumb
    alignItems="center"
    {...animationProp}
    backgroundColor={thumbBackgroundColor}
    justifyContent="center"
    minHeight={SWITCH_THUMB_HEIGHT}
    width={SWITCH_THUMB_HEIGHT}
  >
        <Flex
    $group-item-hover={frameActiveStyle}
    $group-item-press={frameActiveStyle}
    animation="100ms"
    opacity={checked ? 1 : 0}
  >
          <Check color={iconColor} size={CHECK_SIZE} />
        </Flex>

        {
    /* fake thumb for width animation */
  }
        <Flex
    $group-item-hover={outerActiveStyle}
    $group-item-press={outerActiveStyle}
    {...animationProp}
    backgroundColor={thumbBackgroundColor}
    borderRadius="$roundedFull"
    inset={0}
    minHeight={SWITCH_THUMB_HEIGHT}
    position="absolute"
    width={SWITCH_THUMB_HEIGHT}
    zIndex={-2}
  />
      </TamaguiSwitch.Thumb>

      <>
        {
    /* focus ring outer */
  }
        <Flex
    $group-item-focus={{
      borderColor: checked ? isBranded ? "$surface2Hovered" : "$neutral3Hovered" : isBranded ? "$neutral3Hovered" : "$neutral3Hovered"
    }}
    borderColor="transparent"
    borderRadius="$roundedFull"
    borderWidth="$spacing1"
    bottom={OUTER_RING_DISTANCE}
    left={OUTER_RING_DISTANCE}
    position="absolute"
    right={OUTER_RING_DISTANCE}
    top={OUTER_RING_DISTANCE}
    zIndex={-2}
  />

        {
    /* focus ring inner */
  }
        <Flex
    $group-item-focus={{
      borderColor: isBranded ? "$surface1" : "$surface1"
    }}
    borderColor="transparent"
    borderRadius="$roundedFull"
    borderWidth="$spacing2"
    bottom={INNER_RING_DISTANCE}
    left={INNER_RING_DISTANCE}
    position="absolute"
    right={INNER_RING_DISTANCE}
    top={INNER_RING_DISTANCE}
    zIndex={-1}
  />
      </>
    </TamaguiSwitch>;
}
